import type { ElementType, HTMLAttributes, ReactNode } from "react";
import { cn } from "@/components/ui/cn";

type Tone = "default" | "muted" | "primary" | "success" | "warning" | "error";

const toneClass: Record<Tone, string> = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  primary: "text-primary-700",
  success: "text-success-700",
  warning: "text-warning-700",
  error: "text-error-700",
};

type TitleProps = HTMLAttributes<HTMLHeadingElement> & {
  children: ReactNode;
};

export function Title({ className, children, ...props }: TitleProps): React.JSX.Element {
  return (
    <h1 className={cn("text-2xl font-semibold tracking-tight text-foreground", className)} {...props}>
      {children}
    </h1>
  );
}

export function SectionTitle({ className, children, ...props }: TitleProps): React.JSX.Element {
  return (
    <h2 className={cn("text-lg font-semibold tracking-tight text-foreground", className)} {...props}>
      {children}
    </h2>
  );
}

export function CardTitle({ className, children, ...props }: TitleProps): React.JSX.Element {
  return (
    <h3 className={cn("text-base font-semibold text-foreground", className)} {...props}>
      {children}
    </h3>
  );
}

type SubtitleProps = HTMLAttributes<HTMLParagraphElement> & {
  children: ReactNode;
};

export function Subtitle({ className, children, ...props }: SubtitleProps): React.JSX.Element {
  return (
    <p className={cn("text-sm text-muted-foreground", className)} {...props}>
      {children}
    </p>
  );
}

type BodyProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  weight?: "normal" | "medium" | "semibold";
  tone?: Tone;
  children: ReactNode;
};

export function Body({
  as: Comp = "p",
  weight = "normal",
  tone = "default",
  className,
  children,
  ...props
}: BodyProps): React.JSX.Element {
  const weightClass =
    weight === "semibold" ? "font-semibold" : weight === "medium" ? "font-medium" : "font-normal";
  return (
    <Comp className={cn("text-sm", weightClass, toneClass[tone], className)} {...props}>
      {children}
    </Comp>
  );
}

type CaptionProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  tone?: Tone;
  children: ReactNode;
};

export function Caption({
  as: Comp = "span",
  tone = "muted",
  className,
  children,
  ...props
}: CaptionProps): React.JSX.Element {
  return (
    <Comp className={cn("text-xs", toneClass[tone], className)} {...props}>
      {children}
    </Comp>
  );
}
